import React, { useState, useEffect } from 'react';
import {
  Table,
  Tabs,
  Card,
  Statistic,
  Skeleton,
  Form,
  Row,
  Col,
  Select,
} from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import { faListUl } from '@fortawesome/free-solid-svg-icons';

import { useMappedState } from 'redux-react-hook';
import { getInvest } from './../API';

import {
  Bar,
  Line,
  ComposedChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

import {
  buildTotalDeposit,
  getDeposit,
  flattenGrowthResult,
  calculateGrowthSnapshotsFromDeposits,
} from './../utils/invest';

const { Option } = Select;
const { TabPane } = Tabs;

const YEARS = [
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
];

const RATES = [
  1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
  10.5, 11, 11.5, 12, 12.5, 13, 13.5, 14, 14.5, 15, 15.5, 16, 16.5, 17, 17.5,
  18, 18.5, 19, 19.5, 20,
];

const ACCOUNTS = [
  {
    name: 'All',
    value: 'all',
  },
  {
    name: 'Yi/Lin',
    value: 'yi',
  },
  {
    name: 'Grace',
    value: 'grace',
  },
  {
    name: 'Bryan',
    value: 'bryan',
  },
];

function Invest() {
  const session = useMappedState((state) => state.session);
  const [rate, setRate] = useState(null);
  const [startYear, setStartYear] = useState(null);
  const [years, setYears] = useState(null);
  const [invest, setInvest] = useState([]);
  const [deposit, setDeposit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState('all');

  useEffect(() => {
    async function fetchData() {
      const response = await getInvest(session.authToken);
      setRate(response.data.rate);
      setStartYear(response.data.startYear);
      setYears(response.data.years);
      setInvest(response.data.invests);
      setDeposit(response.data.deposits);
      setLoading(false);
    }
    fetchData();
    // eslint-disable-next-line
  }, []);

  const allDeposit = getDeposit(deposit);
  const yiDeposit = getDeposit(deposit, ['yi']);
  const graceDeposit = getDeposit(deposit, ['grace']);
  const bryanDeposit = getDeposit(deposit, ['bryan']);

  const allGrowth = calculateGrowthSnapshotsFromDeposits(
    startYear,
    rate,
    years + 1,
    allDeposit,
  );
  const yiGrowth = calculateGrowthSnapshotsFromDeposits(
    startYear,
    rate,
    years + 1,
    yiDeposit,
  );
  const graceGrowth = calculateGrowthSnapshotsFromDeposits(
    startYear,
    rate,
    years + 1,
    graceDeposit,
  );
  const bryanGrowth = calculateGrowthSnapshotsFromDeposits(
    startYear,
    rate,
    years + 1,
    bryanDeposit,
  );

  const allData = flattenGrowthResult(allGrowth, invest);
  const yiData = flattenGrowthResult(yiGrowth, invest, 'yi');
  const graceData = flattenGrowthResult(graceGrowth, invest, 'grace');
  const bryanData = flattenGrowthResult(bryanGrowth, invest, 'bryan');

  const buildData = (account) => {
    switch (account) {
      case 'all':
        return allData;
      case 'yi':
        return yiData;
      case 'grace':
        return graceData;
      case 'bryan':
        return bryanData;
      default:
        return null;
    }
  };

  const findEstimateEarn = (growthData) => {
    const selectedYears = Object.keys(growthData).map(Number);
    const lastYear = Math.max(...selectedYears);
    const selectedMonths = Object.keys(growthData[lastYear]).map(Number);
    const lastMonth = Math.max(...selectedMonths);
    return growthData[lastYear][lastMonth];
  };

  const buildEstimateEarn = (account) => {
    switch (account) {
      case 'all':
        return findEstimateEarn(allGrowth);
      case 'yi':
        return findEstimateEarn(yiGrowth);
      case 'grace':
        return findEstimateEarn(graceGrowth);
      case 'bryan':
        return findEstimateEarn(bryanGrowth);
      default:
        return null;
    }
  };

  const renderCard = (color, number, text, prefix) => {
    return (
      <Card
        size="small"
        style={{
          marginLeft: '5px',
          marginRight: '5px',
          backgroundColor: color,
        }}
      >
        <Statistic
          title=""
          valueStyle={{ fontSize: '28px', color: '#fff' }}
          value={number}
          prefix={prefix}
        />
        <Row>
          <Col span={18}>
            <div style={{ color: 'white' }}>{text}</div>
          </Col>
        </Row>
      </Card>
    );
  };

  const depositColumns = [
    {
      title: 'Date',
      width: '6%',
      dataIndex: 'date',
      key: 'date',
    },
    {
      title: 'Yi / Lin',
      width: '6%',
      render: (deposit) => (
        <span>
          <i>
            <b>£{deposit.accounts.yi.toLocaleString()}</b>
          </i>
        </span>
      ),
    },
    {
      title: 'Grace',
      width: '6%',
      render: (deposit) => (
        <span>
          <i>
            <b>£{deposit.accounts.grace.toLocaleString()}</b>
          </i>
        </span>
      ),
    },
    {
      title: 'Bryan',
      width: '6%',
      render: (deposit) => (
        <span>
          <i>
            <b>£{deposit.accounts.bryan.toLocaleString()}</b>
          </i>
        </span>
      ),
    },
    {
      title: 'Info',
      width: '15%',
      render: (deposit) => <span>{deposit.meta}</span>,
    },
  ];

  return loading ? (
    <div>
      <p>Loading...</p>
      <Skeleton active />
    </div>
  ) : (
    <div>
      <Row>
        <Col span={6}>
          {renderCard(
            '#B0BF1A',
            buildTotalDeposit(account, deposit),
            `从${startYear}年9月份开始, 总投入资金`,
            '£',
          )}
        </Col>

        <Col span={6}>
          {renderCard('#B284BE', `${rate}%`, '预计平均年化', '')}
        </Col>

        <Col span={6}>
          {renderCard('#72A0C1', `${years} Years`, '预计投资期限', '')}
        </Col>

        <Col span={6}>
          {renderCard(
            '#DE5D83',
            buildEstimateEarn(account),
            `预计${years}年后收益`,
            '£',
          )}
        </Col>
      </Row>

      <br />

      <Row>
        <Col span={7}>
          <Form.Item label="Select Account">
            <Select
              style={{ width: '100%' }}
              value={account}
              onChange={(value) => {
                setAccount(value);
              }}
            >
              {ACCOUNTS.map((i) => (
                <Option key={i.value} value={i.value}>
                  {i.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={1} />
        <Col span={7}>
          <Form.Item label="Select Years">
            <Select
              style={{ width: '100%' }}
              value={years}
              onChange={(value) => {
                setYears(value);
              }}
            >
              {YEARS.map((i) => (
                <Option key={i} value={i}>
                  {i}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={1} />
        <Col span={7}>
          <Form.Item label="Select Rate (%)">
            <Select
              style={{ width: '100%' }}
              value={rate}
              onChange={(value) => {
                setRate(value);
              }}
            >
              {RATES.map((i) => (
                <Option key={i} value={i}>
                  {i}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Tabs onChange={(value) => {}} type="card">
        <TabPane
          tab={
            <div>
              <FontAwesomeIcon icon={faChartBar} />
              &nbsp;Chart
            </div>
          }
          key="chart"
        >
          <ResponsiveContainer width={'100%'} height={350}>
            <ComposedChart
              data={buildData(account)}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 5,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis
                tickFormatter={(value) => {
                  if (value >= 1000000) return `${value / 1000000}M`;
                  if (value >= 1000) return `${value / 1000}k`;
                  return value;
                }}
              />
              <Tooltip cursor={{ fill: '#eee' }} />
              <Legend
                verticalAlign="top"
                align="right"
                wrapperStyle={{
                  left: 0,
                  top: 0,
                  paddingTop: 10,
                }}
              />
              <Bar dataKey="real" fill="#7CB9E8" />
              <Line type="monotone" dataKey="estimate" stroke="#C2B280" />
            </ComposedChart>
          </ResponsiveContainer>
        </TabPane>
        <TabPane
          tab={
            <div>
              <FontAwesomeIcon icon={faListUl} />
              &nbsp;Deposit Details
            </div>
          }
          key="deposit-details"
        >
          <Table
            pagination={false}
            dataSource={[...deposit].reverse()}
            columns={depositColumns}
          />
          ;
        </TabPane>
      </Tabs>
    </div>
  );
}

export default Invest;
